import { useRef, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { usePolicies } from '../../hooks/usePolicies'
import API from '../../services/api.js'
import { motion } from 'framer-motion'
import { ArrowLeft, ArrowRight } from 'lucide-react'

function StatusBadge({ status }) {
  const colours = {
    uploaded: 'bg-white/5 text-gray-400 border border-white/10',
    parsing: 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20',
    parsed: 'bg-blue-500/10 text-blue-400 border border-blue-500/20',
    mapping: 'bg-purple-500/10 text-purple-400 border border-purple-500/20',
    mapped: 'bg-green-500/10 text-green-400 border border-green-500/20',
    failed: 'bg-red-500/10 text-red-400 border border-red-500/20'
  }
  return (
    <span className={`
      px-2 sm:px-3 py-1 rounded-full text-[10px] sm:text-xs
      font-medium inline-flex items-center gap-2
      ${colours[status]}
    `}>
      {status === 'parsing' && (
        <span className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse" />
      )}
      {status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
    </span>
  )
}

/* UPLOAD MODAL */
function UploadModal({ onUpload, onClose, uploading }) {
  const fileInputRef = useRef(null)
  const [file, setFile] = useState(null)
  const [documentType, setDocumentType] = useState('Policy')
  const [customLabel, setCustomLabel] = useState('')
  const [error, setError] = useState(null)

  function handleFileChange(e) {
    const selected = e.target.files[0]
    if (!selected) return
    const allowedExtensions = ['pdf', 'docx', 'txt', 'xlsx']
    const ext = selected.name.split('.').pop().toLowerCase()
    if (!allowedExtensions.includes(ext)) {
      setError('Invalid file type. Allowed: PDF, DOCX, TXT, XLSX')
      return
    }
    if (selected.size > 50 * 1024 * 1024) {
      setError('File too large. Maximum size is 50MB')
      return
    }
    setFile(selected)
    setError(null)
  }

  async function handleSubmit() {
    if (!file) { setError('Please select a file'); return }
    if (documentType === 'Other' && !customLabel.trim()) {
      setError('Please enter a custom label')
      return
    }
    const finalType = documentType === 'Other' ? customLabel.trim() : documentType
    await onUpload(file, finalType)
  }

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className="
          bg-white/5 backdrop-blur-2xl border border-white/10
          rounded-2xl p-4 sm:p-6
          w-[92%] sm:w-full max-w-md shadow-2xl
        "
      >
        <h2 className="text-base sm:text-lg font-semibold text-white mb-4">Upload Document</h2>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">
              File <span className="text-red-400">*</span>
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.txt,.xlsx"
              onChange={handleFileChange}
              className="
                w-full bg-white/5 border border-white/10 rounded-lg
                px-3 py-2 text-sm text-gray-300
                file:mr-3 file:py-1 file:px-3 file:rounded-md file:border-0
                file:text-xs file:bg-white/10 file:text-gray-300
                hover:file:bg-white/20
              "
            />
            {file && (
              <p className="text-xs text-gray-500 mt-1.5">
                {file.name} · {(file.size / 1024 / 1024).toFixed(2)} MB
              </p>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">
              Document Type <span className="text-red-400">*</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {['Policy', 'Meeting Summary', 'Other'].map(type => (
                <button
                  key={type}
                  onClick={() => setDocumentType(type)}
                  className={`
                    flex-1 min-w-[90px] py-1.5 text-xs font-medium rounded-lg transition-all duration-200
                    ${documentType === type
                      ? 'bg-blue-500/20 text-blue-400 border border-blue-500/40'
                      : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10 hover:text-gray-300'
                    }
                  `}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          {documentType === 'Other' && (
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">
                Custom Label <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={customLabel}
                onChange={(e) => setCustomLabel(e.target.value)}
                placeholder="e.g. Audit Report, Risk Assessment..."
                className="
                  w-full bg-white/5 border border-white/10 rounded-lg
                  px-3 py-2 text-sm text-white placeholder-gray-600
                  focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50
                  transition
                "
              />
            </div>
          )}

          <p className="text-xs text-gray-600">
            All document types are parsed and can be mapped to controls.
          </p>
        </div>

        {error && <p className="text-sm text-red-400 mt-3">{error}</p>}

        <div className="flex gap-2 justify-end mt-4">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors">
            Cancel
          </button>
          <motion.button
            onClick={handleSubmit}
            disabled={uploading || !file}
            whileHover={!uploading && file ? { scale: 1.03 } : {}}
            whileTap={!uploading && file ? { scale: 0.97 } : {}}
            className="
              px-4 py-2 rounded-lg text-sm font-medium
              bg-white/10 border border-white/20 text-white
              hover:bg-white/15 hover:border-white/30
              disabled:opacity-40 disabled:cursor-not-allowed
              transition-colors duration-200
            "
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </motion.button>
        </div>
      </motion.div>
    </div>
  )
}

/* HEADER */
function Header({ navigate, onUploadClick, uploading }) {
  return (
    <div className="mb-6 sm:mb-10">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 sm:gap-3">
          <motion.button
            onClick={() => navigate('/grc/framework-review')}
            title="Go back"
            whileHover={{ scale: 1.08, x: -2 }}
            whileTap={{ scale: 0.92 }}
            transition={{ type: 'spring', stiffness: 260, damping: 16 }}
            className="
              inline-flex items-center justify-center
              appearance-none bg-transparent border-0 outline-none
              text-gray-400 hover:text-white transition-colors duration-200
            "
          >
            <ArrowLeft className="w-5 h-5 block shrink-0" />
          </motion.button>

          <div className="flex flex-col">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-white tracking-tight">
              Policies
            </h1>
            <div className="mt-1 h-[2px] w-16 sm:w-32 lg:w-44 bg-gradient-to-r from-blue-500/60 via-blue-400/10 to-transparent animate-pulse" />
          </div>
        </div>

        <motion.button
          onClick={onUploadClick}
          disabled={uploading}
          whileHover={!uploading ? { scale: 1.03 } : {}}
          whileTap={!uploading ? { scale: 0.97 } : {}}
          transition={{ type: 'spring', stiffness: 110, damping: 15, mass: 0.8 }}
          className="
            px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg
            text-[10px] sm:text-sm font-medium
            text-gray-300 bg-white/5 border border-white/10
            hover:bg-white/10 hover:border-white/20
            disabled:opacity-50 focus:outline-none
            transition-colors duration-200
            shrink-0
          "
        >
          {uploading ? 'Uploading...' : 'Upload Document'}
        </motion.button>
      </div>
    </div>
  )
}

export default function Policies() {
  const navigate = useNavigate()
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState(null)
  const [assetId, setAssetId] = useState(null)
  const [resolvingAsset, setResolvingAsset] = useState(true)
  const [retryingId, setRetryingId] = useState(null)
  const selectedAppName = sessionStorage.getItem('selectedApp')

  const { policies, loading, error, uploadPolicy, retryParse } = usePolicies(assetId)

  // Resolve the asset NAME held in sessionStorage into the UUID the
  // backend actually needs. `selectedApp` is set by ApplicationHomepage's
  // handleredirect() as a plain name — see /api/resolve-asset-id/.
  useEffect(() => {
    if (!selectedAppName) {
      setUploadError('No asset selected — navigate to this page via an asset.')
      setResolvingAsset(false)
      return
    }
    setResolvingAsset(true)
    API.get('/resolve-asset-id/', { params: { name: selectedAppName } })
      .then(({ data }) => {
        setAssetId(data.id)
        setUploadError(null)
      })
      .catch(() => {
        setUploadError('Could not resolve the selected asset. Please re-select it from the dashboard.')
      })
      .finally(() => setResolvingAsset(false))
  }, [selectedAppName])

  // Runs once on mount — immediately resets scroll position
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  async function handleUpload(file, documentType) {
    setUploadError(null)
    setUploading(true)
    try {
      setShowUploadModal(false)
      await uploadPolicy(file, assetId, documentType)
    } catch (err) {
      setUploadError(err.message)
    } finally {
      setUploading(false)
    }
  }

  async function handleRetry(policyId) {
    setRetryingId(policyId)
    try {
      await retryParse(policyId)
    } catch (err) {
      setUploadError(err.message)
    } finally {
      setRetryingId(null)
    }
  }

  if (loading) {
    return (
      <div className="fixed inset-0 z-[9999] bg-black flex items-center justify-center overflow-hidden">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-white/10 border-t-blue-400 rounded-full animate-spin" />
          <p className="text-sm text-gray-400">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="relative min-h-screen overflow-hidden">

      {showUploadModal && (
        <UploadModal
          onUpload={handleUpload}
          onClose={() => setShowUploadModal(false)}
          uploading={uploading}
        />
      )}

      <div className="absolute inset-0 bg-cover bg-center" />
      <div className="absolute inset-0 bg-black/80" />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-3 md:px-4 lg:px-6 py-6 sm:py-8 min-w-0">

        <Header
          navigate={navigate}
          onUploadClick={() => setShowUploadModal(true)}
          uploading={uploading || resolvingAsset || !assetId}
        />

        {(uploadError || error || uploading) && (
          <div className="mb-5 sm:mb-6 bg-white/5 border border-white/10 text-gray-300 px-3 sm:px-4 py-3 rounded-lg text-xs sm:text-sm flex items-center gap-3">
            <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
            {uploadError || error || 'Uploading and processing document...'}
          </div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="bg-white/5 backdrop-blur-2xl border border-white/10 rounded-xl sm:rounded-2xl overflow-hidden"
        >
          {!loading && !policies?.length ? (
            <div className="text-center py-14 sm:py-16 text-gray-500 px-4">
              <p className="text-base sm:text-lg text-gray-300">No policies yet</p>
              <p className="text-xs sm:text-sm mt-2">Upload your first document to begin</p>
            </div>
          ) : (
            <>
              <div className="hidden md:block">
                <table className="w-full table-fixed text-sm">
                  <colgroup>
                    <col className="w-[33%]" />
                    <col className="w-[10%]" />
                    <col className="w-[15%]" />
                    <col className="w-[15%]" />
                    <col className="w-[12%]" />
                    <col className="w-[15%] hidden lg:table-column" />
                  </colgroup>
                  <thead>
                    <tr className="border-b border-white/10">
                      {['Name', 'Type', 'Status', 'Label', 'Uploaded', ''].map(col => (
                        <th
                          key={col}
                          className={`
                            text-left px-3 lg:px-5 py-3 text-[11px] font-medium
                            text-gray-500 uppercase tracking-wider
                            ${col === '' ? 'hidden lg:table-cell' : ''}
                          `}
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {policies.map(policy => {
                      const isNavigable = policy.status !== 'uploaded' && policy.status !== 'parsing'
                      return (
                        <tr
                          key={policy.id}
                          onClick={() => isNavigable && navigate(`/grc/policies/${policy.id}`)}
                          className={`
                            group hover:bg-white/5 transition
                            md:cursor-pointer lg:cursor-default
                            ${!isNavigable ? 'md:cursor-not-allowed opacity-60' : ''}
                          `}
                        >
                          <td className="px-3 lg:px-5 py-4 font-medium text-white text-sm truncate">
                            {policy.name}
                          </td>
                          <td className="px-3 lg:px-5 py-4 text-gray-500 uppercase text-[11px]">
                            {policy.file_type}
                          </td>
                          <td className="px-3 lg:px-5 py-4">
                            <StatusBadge status={policy.status} />
                          </td>
                          <td className="px-3 lg:px-5 py-4">
                            <span className="px-2 py-0.5 rounded-full text-[11px] bg-white/5 border border-white/10 text-gray-400 truncate block w-fit max-w-full">
                              {policy.document_type || 'Policy'}
                            </span>
                          </td>
                          <td className="px-3 lg:px-5 py-4 text-gray-500 text-[11px]">
                            {new Date(policy.uploaded_at).toLocaleDateString()}
                          </td>
                          <td className="px-3 lg:px-5 py-4 text-right hidden lg:table-cell">
                            {policy.status === 'failed' && (
                              <motion.button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleRetry(policy.id)
                                }}
                                disabled={retryingId === policy.id}
                                whileHover={{ scale: 1.04 }}
                                whileTap={{ scale: 0.96 }}
                                className="
                                  inline-flex items-center gap-1.5
                                  px-3 py-1.5 mr-2
                                  text-[11px] sm:text-xs
                                  bg-red-500/10 border border-red-500/20
                                  rounded-md text-red-400
                                  hover:bg-red-500/20
                                  disabled:opacity-40 disabled:cursor-not-allowed
                                  focus:outline-none transition-colors duration-200
                                "
                              >
                                {retryingId === policy.id ? 'Retrying...' : 'Retry'}
                              </motion.button>
                            )}
                            <motion.button
                              onClick={(e) => {
                                e.stopPropagation()
                                navigate(`/grc/policies/${policy.id}`)
                              }}
                              disabled={!isNavigable}
                              whileHover={isNavigable ? { scale: 1.04 } : {}}
                              whileTap={isNavigable ? { scale: 0.96 } : {}}
                              transition={{
                                type: 'spring',
                                stiffness: 120,
                                damping: 16,
                                mass: 0.8,
                              }}
                              className="
                                inline-flex items-center gap-1.5
                                px-3 py-1.5
                                w-fit whitespace-nowrap
                                text-[11px] sm:text-xs
                                bg-white/5 border border-white/10
                                rounded-md text-gray-300
                                hover:text-white hover:border-gray-500/50 hover:bg-white/10
                                disabled:opacity-30 disabled:cursor-not-allowed
                                focus:outline-none transition-colors duration-200
                              "
                            >
                              <span className="whitespace-nowrap">View Clauses</span>
                              <ArrowRight className="w-3.5 h-3.5 shrink-0" />
                            </motion.button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              <div className="md:hidden space-y-3 p-3">
                {policies.map(policy => (
                  <div
                    key={policy.id}
                    className="bg-white/5 border border-white/10 rounded-xl p-4"
                  >
                    <div className="flex justify-between items-start gap-3">
                      <h3 className="text-white font-medium text-sm leading-relaxed">{policy.name}</h3>
                      <StatusBadge status={policy.status} />
                    </div>

                    <div className="mt-3 flex flex-wrap gap-2">
                      <span className="text-xs text-gray-500 uppercase">{policy.file_type}</span>
                      <span className="px-2 py-0.5 rounded-full text-[11px] bg-white/5 border border-white/10 text-gray-400 mt-[-3px]">
                        {policy.document_type || 'Policy'}
                      </span>
                    </div>

                    <div className="mt-2 text-xs text-gray-500">
                      {new Date(policy.uploaded_at).toLocaleDateString()}
                    </div>

                    {policy.status === 'failed' && (
                      <motion.button
                        onClick={() => handleRetry(policy.id)}
                        disabled={retryingId === policy.id}
                        className="
                          mt-4 w-full flex items-center justify-center gap-2
                          px-4 py-2 rounded-lg bg-red-500/10 border border-red-500/20
                          text-red-400 text-sm
                          disabled:opacity-40 disabled:cursor-not-allowed
                          hover:bg-red-500/20 transition-colors duration-200
                        "
                      >
                        {retryingId === policy.id ? 'Retrying...' : 'Retry Parsing'}
                      </motion.button>
                    )}
                    <motion.button
                      onClick={() => navigate(`/grc/policies/${policy.id}`)}
                      disabled={policy.status === 'uploaded' || policy.status === 'parsing'}
                      className="
                        mt-4 w-full flex items-center justify-center gap-2
                        px-4 py-2 rounded-lg bg-white/5 border border-white/10
                        text-gray-300 text-sm
                        disabled:opacity-30 disabled:cursor-not-allowed
                        hover:bg-white/10 transition-colors duration-200
                      "
                    >
                      View Clauses
                      <ArrowRight className="w-4 h-4 mt-1" />
                    </motion.button>
                  </div>
                ))}
              </div>
            </>
          )}
        </motion.div>
      </div>
    </div>
  )
}
